import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('multi-accounting');

/**
 * The strongest page in cluster C, and the one closest to the product — which is exactly why it
 * has to stay an explanation of the TECHNIQUE rather than of our implementation. The worked
 * canonicalisation examples are real (they mirror `canonicalize` in @sieve/shared); the page
 * describes what any correct implementation must do, not what ours does differently.
 */
export default function MultiAccountingPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Multi-accounting is one person creating many accounts on the same service, usually to repeat a signup benefit. It is trivial because most providers treat several different-looking addresses as the same mailbox: Gmail ignores dots, most providers ignore anything after a plus sign, and both variants deliver to the same inbox.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Why one mailbox produces unlimited addresses">
        <p>
          Two conventions, both entirely legitimate and both widely supported, mean a single inbox
          answers to an unbounded set of addresses. No tooling required — a person can type these.
        </p>
        <LearnTable
          head={['Convention', 'Example', 'Delivers to', 'Supported by']}
          rows={[
            ['Plus-addressing', <Tok key="a">name+shop@gmail.com</Tok>, <Tok key="b">name@gmail.com</Tok>, 'Gmail, Outlook, Fastmail, Proton, most modern hosts'],
            ['Dot-folding', <Tok key="c">n.a.m.e@gmail.com</Tok>, <Tok key="d">name@gmail.com</Tok>, <Lead key="e">Gmail only</Lead>],
            ['Domain alias', <Tok key="f">name@googlemail.com</Tok>, <Tok key="g">name@gmail.com</Tok>, 'Gmail'],
            ['Case', <Tok key="h">Name@Gmail.com</Tok>, <Tok key="i">name@gmail.com</Tok>, 'Everyone — the domain is case-insensitive'],
          ]}
        />
        <p>
          Dot-folding is the one that surprises people, and it is Gmail-specific. Combine it with
          plus-addressing on an eight-character local part and one mailbox yields well over a
          hundred distinct-looking addresses that all arrive in the same place.
        </p>
        <LearnCode>
          {`all of these reach exactly one inbox:

  jane.doe@gmail.com
  janedoe@gmail.com
  j.a.n.e.d.o.e@gmail.com
  janedoe+signup@gmail.com
  jane.doe+trial2@googlemail.com
  JaneDoe@Gmail.com`}
        </LearnCode>
      </LearnSection>

      <LearnSection title="Canonicalisation: collapsing them to one key">
        <p>
          The defence is not to block these addresses — plus-addressing is a good practice used by
          careful people. It is to compute a <strong className="text-txt">canonical mailbox key</strong>{' '}
          and recognise that six signups map to one of them.
        </p>
        <p>The steps, in the order they must run:</p>
        <LearnList ordered>
          <li>
            <Lead>Lowercase the domain.</Lead> Always safe — domains are case-insensitive by
            specification.
          </li>
          <li>
            <Lead>Map known domain aliases.</Lead> <Tok>googlemail.com</Tok> →{' '}
            <Tok>gmail.com</Tok>. A short list, not a general rule.
          </li>
          <li>
            <Lead>Strip the plus tag</Lead> — everything from the first <Tok>+</Tok> to the{' '}
            <Tok>@</Tok> — for providers that support it.
          </li>
          <li>
            <Lead>Remove dots from the local part</Lead>, but <em>only</em> for Gmail. This step is
            wrong everywhere else.
          </li>
          <li>
            <Lead>Lowercase the local part</Lead> for providers that treat it case-insensitively,
            which in practice is all the major consumer ones.
          </li>
        </LearnList>
        <LearnCode>
          {`jane.doe+trial2@googlemail.com
  ↓ lowercase domain          jane.doe+trial2@googlemail.com
  ↓ alias → gmail.com         jane.doe+trial2@gmail.com
  ↓ strip +tag                jane.doe@gmail.com
  ↓ remove dots (Gmail only)  janedoe@gmail.com
  ↓ lowercase local           janedoe@gmail.com   ← canonical key`}
        </LearnCode>
        <p>
          Store the canonical key alongside the address the user typed. Send to what they gave you;
          count uniqueness on the key. That distinction is the whole design — canonicalising for
          delivery would break the plus-tag filters people rely on.
        </p>
      </LearnSection>

      <LearnSection title="Where step 4 goes wrong">
        <p>
          Dot-removal is the step most implementations get wrong, and the failure is silent and
          expensive: two genuinely different people collapse into one account.
        </p>
        <LearnTable
          head={['Address pair', 'Same mailbox?', 'Why']}
          rows={[
            [<span key="a"><Tok>j.smith@gmail.com</Tok> / <Tok>jsmith@gmail.com</Tok></span>, <Lead key="b">Yes</Lead>, 'Gmail folds dots'],
            [<span key="c"><Tok>j.smith@outlook.com</Tok> / <Tok>jsmith@outlook.com</Tok></span>, <Lead key="d">No — different people</Lead>, 'Outlook treats dots as significant'],
            [<span key="e"><Tok>j.smith@company.com</Tok> / <Tok>jsmith@company.com</Tok></span>, <Lead key="f">No</Lead>, 'Corporate domains almost never fold'],
            [<span key="g"><Tok>a+x@gmail.com</Tok> / <Tok>a+y@gmail.com</Tok></span>, <Lead key="h">Yes</Lead>, 'Plus tags are universally strippable'],
          ]}
        />
        <p>
          Rows two and three are the ones that cost you. Applying Gmail's rule to a corporate domain
          merges <Tok>j.smith</Tok> and <Tok>jsmith</Tok> — plausibly two colleagues — into a single
          identity, and they then cannot both hold accounts. Dot-folding must be gated on the
          provider, never applied globally.
        </p>
      </LearnSection>

      <LearnSection title="What canonicalisation does not catch">
        <p>
          It closes the free variants. Someone willing to spend a little more has other routes, and
          being clear about the ceiling is more useful than implying there isn't one:
        </p>
        <LearnList>
          <li>
            <Lead>Genuinely separate mailboxes.</Lead> Five real Gmail accounts are five real
            mailboxes. No address-level analysis will merge them.
          </li>
          <li>
            <Lead>
              <Link to="/learn/disposable-email-addresses" className="text-accent underline underline-offset-4">
                Disposable addresses
              </Link>
              .
            </Lead>{' '}
            A different problem with a different defence — the domain, not the local part.
          </li>
          <li>
            <Lead>Their own domain.</Lead> Anyone with a catch-all domain has unlimited genuinely
            distinct addresses, and they are indistinguishable from a small company's staff.
          </li>
        </LearnList>
        <p>
          Which is why address canonicalisation is a first layer rather than the answer. Beyond it
          the signals are behavioural — timing, near-duplicate local parts across different domains,
          shared device or network characteristics — and{' '}
          <Link to="/features/bot-detection" className="text-accent underline underline-offset-4">
            clustering those
          </Link>{' '}
          is what catches the cases canonicalisation cannot.
        </p>
      </LearnSection>

      <LearnSection title="Not everything that looks like this is abuse">
        <LearnSubsection title="Plus-addressing is good practice">
          <p>
            Using <Tok>name+yourservice@</Tok> is exactly what the convention is for: it lets
            someone filter your mail and see who leaked their address. Treating it as suspicious
            punishes your most careful users.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Shared mailboxes are normal">
          <p>
            Several signups from{' '}
            <Link to="/learn/role-based-email-addresses" className="text-accent underline underline-offset-4">
              a role address
            </Link>{' '}
            usually means several colleagues, not one person twice.
          </p>
        </LearnSubsection>
        <LearnSubsection title="The right response is rarely a block">
          <p>
            Detecting that six accounts share one mailbox is useful information. Refusing the
            signup outright turns a false positive into a lost customer with no recourse. Limiting
            the <em>benefit</em> to one per canonical mailbox — one trial, one discount — gets the
            outcome without that cost.
          </p>
        </LearnSubsection>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is multi-accounting?',
    a: 'One person creating multiple accounts on the same service, usually to claim a signup benefit more than once. It is easy because most providers deliver several different-looking addresses to the same mailbox, so the accounts look unrelated in your database.',
  },
  {
    q: 'Does Gmail really ignore dots in email addresses?',
    a: 'Yes. jane.doe@gmail.com and janedoe@gmail.com are the same mailbox, and so is j.a.n.e.d.o.e@gmail.com. This is Gmail-specific — Outlook and virtually all corporate domains treat dots as significant, so applying the rule everywhere merges genuinely different people.',
  },
  {
    q: 'Should I block plus-addressing at signup?',
    a: 'No. It is a legitimate convention that lets people filter mail and identify who leaked their address, so blocking it punishes your most careful users. Strip the tag when computing a uniqueness key, but keep the address they gave you for sending.',
  },
  {
    q: 'What is email canonicalisation?',
    a: 'Reducing an address to a single key representing the underlying mailbox: lowercase the domain, map known aliases, strip the plus tag, remove dots for Gmail only, lowercase the local part. Six variants collapse to one key, so you can count uniqueness correctly.',
  },
  {
    q: 'Can canonicalisation stop all multi-accounting?',
    a: 'No, and it is not meant to. It closes the free variants of one mailbox. Someone with five real accounts, or their own catch-all domain, has genuinely distinct addresses that no address-level analysis will merge — those need behavioural signals instead.',
  },
  {
    q: 'Should I block accounts that share a canonical mailbox?',
    a: 'Usually not. Blocking turns a false positive into a lost customer with no recourse, and there are innocent explanations. Limiting the benefit rather than the account — one trial or one discount per canonical mailbox — achieves the goal without that risk.',
  },
];
